import { useEffect, useState } from 'react';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { format, parseISO } from 'date-fns';

import { Currency, DropdownInput, DropdownOption } from '@aslanpay/portal-common-lib';

import MoneyAmountInput from 'app/components/UI/Inputs/MoneyAmountInput/MoneyAmountInput';

import useCreateDeductionAdjustment from 'app/hooks/useCreateDeductionAdjustment';

import { deductionAdjustmentSchema } from 'app/utils/schemas';
import { formatCurrency } from 'app/utils/strings';
import { DEDUCTION_ADJUSTMENT_NOTES_MAX_LENGTH } from 'app/utils/schemas/deductionAdjustment';

import { AddDeductionAdjustmentModalProps, DeductionAdjustmentFormFields } from './types';

const DIRECTION_OPTIONS: DropdownOption[] = [
  { value: 'DEDUCT', text: 'Deduct more' },
  { value: 'NET_ADDITION', text: 'Net addition' },
];

const DEFAULT_VALUES: DeductionAdjustmentFormFields = {
  direction: undefined,
  amount: '',
  notes: '',
};

/**
 * Describes a deduction as it reads on the report: an amount to deduct, or a net addition when it is negative.
 */
const describeDeduction = (deduction: number, currency: Currency) =>
  deduction < 0
    ? `${formatCurrency(-deduction, currency)} net addition`
    : formatCurrency(deduction, currency);

/** Asks an Aslan admin for an amount to deduct more, or add as a net addition, on a person's open report, and why. */
function AddDeductionAdjustmentModal({
  isOpen,
  onClose,
  individualId,
  individualName = undefined,
  openReport,
}: AddDeductionAdjustmentModalProps) {
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const { mutate: createAdjustment, isLoading: isSaving } = useCreateDeductionAdjustment();

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<DeductionAdjustmentFormFields>({
    resolver: yupResolver(deductionAdjustmentSchema),
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (isOpen) {
      setIdempotencyKey(crypto.randomUUID());
      reset(DEFAULT_VALUES);
    }
  }, [isOpen, reset]);

  const notes = watch('notes');
  const direction = watch('direction');
  const amount = parseFloat(watch('amount')) || 0;
  const signedAmount = direction === 'NET_ADDITION' ? -amount : amount;
  const newDeduction = direction ? openReport.currentDeduction + signedAmount : undefined;

  const onSubmit = (data: DeductionAdjustmentFormFields) => {
    if (!data.direction) {
      return;
    }
    createAdjustment(
      {
        individualId,
        direction: data.direction,
        amount: parseFloat(data.amount),
        notes: data.notes.trim(),
        idempotencyKey,
      },
      { onSuccess: onClose }
    );
  };

  const cutoff = openReport.cutoffDate
    ? `, cut-off ${format(parseISO(openReport.cutoffDate), 'd MMMM yyyy')}`
    : '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc={!isSaving}
      closeOnOverlayClick={!isSaving}
      size="xl"
      isCentered
    >
      <ModalOverlay />
      <ModalContent data-testid="add-deduction-adjustment-modal">
        <ModalHeader>
          Add deduction adjustment{individualName ? ` – ${individualName}` : ''}
        </ModalHeader>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <ModalBody>
            <Stack spacing="16px">
              <Alert status="warning" borderRadius="8px" alignItems="flex-start">
                <AlertIcon />
                <AlertDescription data-testid="adjustment-warning">
                  This does not change their wallet balance. It only changes what is deducted from
                  their pay on the {openReport.payCycleName} deduction report{cutoff}.
                </AlertDescription>
              </Alert>
              <HStack spacing="40px" data-testid="adjustment-deduction-preview">
                <Stack spacing="2px">
                  <Text textStyle="smallMedium" color="brand.darkGray">
                    Current deduction
                  </Text>
                  <Text textStyle="h5SemiBold" data-testid="adjustment-current-deduction">
                    {describeDeduction(openReport.currentDeduction, openReport.currency)}
                  </Text>
                </Stack>
                <Stack spacing="2px">
                  <Text textStyle="smallMedium" color="brand.darkGray">
                    New deduction
                  </Text>
                  <Text textStyle="h5SemiBold" data-testid="adjustment-new-deduction">
                    {newDeduction === undefined
                      ? '-'
                      : describeDeduction(newDeduction, openReport.currency)}
                  </Text>
                </Stack>
              </HStack>
              <FormControl isInvalid={!!errors.direction} isRequired>
                <FormLabel>Adjustment</FormLabel>
                <Controller
                  control={control}
                  name="direction"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <DropdownInput
                      placeholder="Select an adjustment"
                      defaultValue={DIRECTION_OPTIONS.find((option) => option.value === value)}
                      options={DIRECTION_OPTIONS}
                      onChange={(option) => onChange(option != null ? String(option) : undefined)}
                      onBlur={onBlur}
                      isDisabled={isSaving}
                    />
                  )}
                />
                <FormHelperText>
                  Deduct more takes the amount off their next payslip; a net addition adds it to
                  their pay.
                </FormHelperText>
                <FormErrorMessage>{errors.direction?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.amount} isRequired>
                <FormLabel>Amount</FormLabel>
                <Controller
                  control={control}
                  name="amount"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <MoneyAmountInput
                      currency={openReport.currency}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      isDisabled={isSaving}
                      isInvalid={!!errors.amount}
                      size="md"
                    />
                  )}
                />
                <FormHelperText>Up to £5,000.</FormHelperText>
                <FormErrorMessage>{errors.amount?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.notes} isRequired>
                <FormLabel>Reason</FormLabel>
                <Textarea
                  {...register('notes')}
                  maxLength={DEDUCTION_ADJUSTMENT_NOTES_MAX_LENGTH}
                  placeholder="For example, Set & Spend was counted twice on the 9 October report"
                  isDisabled={isSaving}
                  data-testid="adjustment-notes-input"
                />
                <FormHelperText display="flex" justifyContent="space-between" gap="12px">
                  <Text as="span" data-testid="adjustment-notes-audit-note">
                    For internal auditing only. Clients and employees never see it.
                  </Text>
                  <Text as="span" flexShrink={0}>
                    {notes.length} / {DEDUCTION_ADJUSTMENT_NOTES_MAX_LENGTH}
                  </Text>
                </FormHelperText>
                <FormErrorMessage>{errors.notes?.message}</FormErrorMessage>
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter gap="12px">
            <Button
              variant="outline"
              onClick={onClose}
              isDisabled={isSaving}
              data-testid="cancel-deduction-adjustment-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isSaving}
              isDisabled={!isValid || isSaving}
              data-testid="confirm-deduction-adjustment-button"
            >
              Add adjustment
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

export default AddDeductionAdjustmentModal;
